import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Textarea,
} from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { IconEye, IconReceipt, IconCheck, IconPlus } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import { intelligenceApi, type IntelligenceProduct } from '../../api/intelligence'
import { Empty, IntelligencePageHeader, QueryState } from './shared'
import { asRecords, idOf, label, titleOf } from './utils'

function SemanticContract({ product }: { product: IntelligenceProduct }) {
  const items = [
    { label: 'Metrics', value: product.metrics_label },
    { label: 'Dimensions', value: product.dimensions_label },
    { label: 'Time grain', value: product.time_grain_label },
  ]
  if (!items.some((item) => item.value)) return null
  return (
    <Card bg="teal.0" p="sm" withBorder={false}>
      <SimpleGrid cols={3}>
        {items.map((item) =>
          item.value ? (
            <div key={item.label}>
              <Text size="xs" fw={700} c="teal.8">
                {item.label}
              </Text>
              <Text size="xs" c="teal.9">
                {item.value}
              </Text>
            </div>
          ) : null
        )}
      </SimpleGrid>
    </Card>
  )
}

function ProductTags({ product }: { product: IntelligenceProduct }) {
  if (!product.tags?.length) return null
  return (
    <Group gap="xs">
      {product.tags.map((tag) => (
        <Badge key={tag} size="xs" variant="light" color="gray">
          {tag}
        </Badge>
      ))}
    </Group>
  )
}

export function Products() {
  const [cart, setCart] = useState<Record<string, unknown>[]>([])
  const [purpose, setPurpose] = useState('')
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const products = useQuery({
    queryKey: ['intelligence', 'products'],
    queryFn: intelligenceApi.listProducts,
  })
  const detail = useQuery({
    queryKey: ['intelligence', 'product', detailId],
    queryFn: () => intelligenceApi.getProduct(detailId ?? ''),
    enabled: Boolean(detailId),
  })
  const checkout = useMutation({
    mutationFn: () => intelligenceApi.checkout(cart.map(idOf), purpose),
    onSuccess: (data) => {
      setCart([])
      setPurpose('')
      setCheckoutOpen(false)
      setDetailId(null)
      showNotification({
        title: 'Product request submitted',
        message: `Request ${data.request_id} is now ${data.status}.`,
        color: 'green',
      })
    },
  })
  const rows = asRecords(products.data)
  const cartIds = useMemo(() => new Set(cart.map(idOf)), [cart])
  const toggleCart = (product: Record<string, unknown>) =>
    setCart((current) =>
      cartIds.has(idOf(product))
        ? current.filter((item) => idOf(item) !== idOf(product))
        : [...current, product]
    )
  const selectedProduct = products.data?.find((p) => p.id === detailId)

  return (
    <>
      <Stack>
        <IntelligencePageHeader
          title="Data catalog"
          description="Discover governed data products and request access for your team."
          action={
            <Button
              leftSection={<IconReceipt size={16} />}
              color="prism"
              variant="light"
              onClick={() => setCheckoutOpen(true)}
              disabled={!cart.length}
            >
              Product requests {cart.length ? `(${cart.length})` : ''}
            </Button>
          }
        />
        <QueryState error={products.error} loading={products.isLoading} />
        {!products.isLoading &&
          !products.error &&
          (rows.length ? (
            <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }}>
              {rows.map((product) => {
                const typed = product as unknown as IntelligenceProduct
                const inCart = cartIds.has(idOf(product))
                return (
                  <Card key={idOf(product)} withBorder padding="lg">
                    <Stack gap="sm">
                      <Group justify="space-between" align="flex-start">
                        <Text fw={700}>{titleOf(product)}</Text>
                        <Group gap="xs">
                          {typed.product_type && (
                            <Badge color="prism" variant="light" size="sm">
                              {typed.product_type}
                            </Badge>
                          )}
                          <Badge color="violet" size="sm">
                            {label(product.domain)}
                          </Badge>
                        </Group>
                      </Group>
                      <ProductTags product={typed} />
                      <SemanticContract product={typed} />
                      <Text size="sm" c="dimmed">
                        {label(product.description)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        Owner: {label(product.owner)}
                      </Text>
                      <Group grow>
                        <Button
                          variant="light"
                          color="prism"
                          size="sm"
                          leftSection={<IconEye size={16} />}
                          onClick={() => setDetailId(idOf(product))}
                        >
                          View details
                        </Button>
                      </Group>
                      <Checkbox
                        checked={inCart}
                        label="Add to request"
                        onChange={() => toggleCart(product)}
                      />
                    </Stack>
                  </Card>
                )
              })}
            </SimpleGrid>
          ) : (
            <Empty message="No governed products are available." />
          ))}
      </Stack>

      <Modal
        opened={Boolean(detailId)}
        onClose={() => setDetailId(null)}
        title={selectedProduct?.name ?? 'Product details'}
        size="lg"
        centered
      >
        <QueryState error={detail.error} loading={detail.isLoading} />
        {!detail.isLoading && !detail.error && detail.data && (
          <Stack>
            <Group gap="xs">
              {detail.data.product_type && (
                <Badge color="prism" variant="light">
                  {detail.data.product_type}
                </Badge>
              )}
              <Badge color="violet">{label(detail.data.domain)}</Badge>
              {detail.data.availability && (
                <Badge color="teal" variant="light">
                  {detail.data.availability}
                </Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              {label(detail.data.description)}
            </Text>
            <SemanticContract product={detail.data} />
            <Tabs defaultValue="overview">
              <Tabs.List>
                <Tabs.Tab value="overview">Overview</Tabs.Tab>
                <Tabs.Tab value="guide">Guide</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="overview" pt="sm">
                <Stack gap="sm">
                  {detail.data.use_cases && detail.data.use_cases.length > 0 && (
                    <div>
                      <Text size="xs" fw={700} c="gray.6" mb="xs">
                        Use cases
                      </Text>
                      {detail.data.use_cases.map((useCase) => (
                        <Group key={useCase} gap="xs" mb="xs">
                          <IconCheck size={16} color="var(--mantine-color-teal-6)" />
                          <Text size="sm">{useCase}</Text>
                        </Group>
                      ))}
                    </div>
                  )}
                  {detail.data.highlights && detail.data.highlights.length > 0 && (
                    <div>
                      <Text size="xs" fw={700} c="gray.6" mb="xs">
                        Highlights
                      </Text>
                      {detail.data.highlights.map((highlight) => (
                        <Group key={highlight} gap="xs" mb="xs">
                          <IconPlus size={16} color="var(--mantine-color-prism-6)" />
                          <Text size="sm">{highlight}</Text>
                        </Group>
                      ))}
                    </div>
                  )}
                  <SimpleGrid cols={{ base: 1, sm: 3 }}>
                    <div>
                      <Text size="xs" c="gray.6">
                        Access
                      </Text>
                      <Text size="sm">{label(detail.data.access_method)}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="gray.6">
                        Coverage
                      </Text>
                      <Text size="sm">{label(detail.data.coverage)}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="gray.6">
                        Refresh
                      </Text>
                      <Text size="sm">{label(detail.data.refresh_cadence)}</Text>
                    </div>
                  </SimpleGrid>
                  <ProductTags product={detail.data} />
                </Stack>
              </Tabs.Panel>
              <Tabs.Panel value="guide" pt="sm">
                {detail.data.documentation?.length ? (
                  <Stack gap="sm">
                    {detail.data.documentation.map((section) => (
                      <Card key={section.title} withBorder p="sm">
                        <Text size="sm" fw={700}>
                          {section.title}
                        </Text>
                        <Text size="xs" c="gray.6" mb="xs">
                          {section.summary}
                        </Text>
                        {section.items.map((item) => (
                          <Text key={item} size="xs" c="gray.7" mb="xs">
                            • {item}
                          </Text>
                        ))}
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Empty message="No documentation is available for this product." />
                )}
              </Tabs.Panel>
            </Tabs>
            <Checkbox
              checked={cartIds.has(idOf(detail.data as unknown as Record<string, unknown>))}
              label="Add to request"
              onChange={() =>
                toggleCart(detail.data as unknown as Record<string, unknown>)
              }
            />
          </Stack>
        )}
      </Modal>

      <Modal
        opened={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        title="Submit data product request"
        centered
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {cart.length} selected product{cart.length === 1 ? '' : 's'} will be
            sent for access review.
          </Text>
          <Textarea
            label="Business purpose"
            value={purpose}
            onChange={(event) => setPurpose(event.currentTarget.value)}
            required
          />
          <Button
            onClick={() => checkout.mutate()}
            loading={checkout.isLoading}
            disabled={!purpose.trim()}
          >
            Submit request
          </Button>
          {checkout.error ? (
            <Alert color="red">
              {checkout.error instanceof Error
                ? checkout.error.message
                : 'The request could not be submitted.'}
            </Alert>
          ) : null}
        </Stack>
      </Modal>
    </>
  )
}
